// ── cohort-sessions-collection: schedule adapter for sessions tab ───────────

import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  StatusDot,
  useCollection,
  type CollectionColumnDef,
  type CollectionFilterDef,
  type UseCollectionResult
} from "@vjal/ui";

import type { CoachingSessionDto } from "../../../services/coaching-dtos";
import { orDash } from "../cohort-detail-helpers";
import {
  inviteStatusLabel,
  inviteStatusTone,
  sessionDisplayTitle,
  sessionStatusLabel,
  sessionStatusTone
} from "../../sessions/sessions-helpers";
import { COHORT_TAB_PAGE_SIZE } from "./cohort-tab-collection-shared";

export type SessionRow = CoachingSessionDto & Record<string, unknown>;

export function statusOptionsFromSessions(rows: CoachingSessionDto[]): string[] {
  const seen = new Set<string>();
  for (const row of rows) {
    const status = (row.status ?? "").trim();
    if (status) seen.add(status);
  }
  return Array.from(seen).sort();
}

export function buildSessionsFilterDefs(rows: CoachingSessionDto[]): CollectionFilterDef<SessionRow>[] {
  const statusOptions = statusOptionsFromSessions(rows);
  return [
    {
      id: "status",
      label: "Status",
      type: "select",
      placeholder: "All statuses",
      getValue: (row) => row.status ?? "scheduled",
      options: statusOptions.map((status) => ({ value: status, label: sessionStatusLabel(status) }))
    },
    {
      id: "invite_status",
      label: "Invite",
      type: "select",
      placeholder: "All invite states",
      getValue: (row) => row.invite_status ?? "pending",
      options: Array.from(new Set(rows.map((row) => row.invite_status ?? "pending"))).map((status) => ({
        value: status,
        label: inviteStatusLabel(status)
      }))
    }
  ];
}

export function buildSessionsCollectionColumns(): CollectionColumnDef<SessionRow>[] {
  return [
    {
      id: "title",
      header: "Session",
      searchValue: (row) => `${row.title ?? ""} ${row.id}`,
      priority: 1,
      cell: (row) => (
        <Link
          to={`/sessions/${encodeURIComponent(row.id)}`}
          className="font-medium text-link no-underline hover:underline"
          data-exact-set-name="Open session">
          {sessionDisplayTitle(row.title, row.id)}
        </Link>
      )
    },
    {
      id: "starts_at",
      header: "Starts",
      priority: 2,
      cell: (row) => orDash(row.starts_at)
    },
    {
      id: "invite_status",
      header: "Invite",
      priority: 3,
      cell: (row) => <Badge variant={inviteStatusTone(row.invite_status)}>{inviteStatusLabel(row.invite_status)}</Badge>
    },
    {
      id: "status",
      header: "Status",
      priority: 2,
      cell: (row) => (
        <StatusDot
          tone={sessionStatusTone(row.status) === "danger" ? "err" : "info"}
          label={sessionStatusLabel(row.status)}
         />
      )
    }
  ];
}

export function useCohortSessionsCollection(rows: CoachingSessionDto[]): UseCollectionResult<SessionRow> {
  const filters = useMemo(() => buildSessionsFilterDefs(rows), [rows]);
  const columns = useMemo(() => buildSessionsCollectionColumns(), []);

  return useCollection<SessionRow>({
    mode: "client",
    rows: rows as SessionRow[],
    columns,
    filters,
    pageSize: COHORT_TAB_PAGE_SIZE,
    urlSync: false,
    getRowKey: (row) => row.id
  });
}
